import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { CALENDLY_DEMO_URL } from "@/constants/links";

import heroImg from "@/assets/interior-inspiration.jpg";
import livingRoomImg from "@/assets/living-room-wall.jpg";
import armchairImg from "@/assets/elegant-armchair.jpg";
import sculptureImg from "@/assets/luxury-sculpture.jpg";
import wabiSabiImg from "@/assets/wabi-sabi-lighting.jpg";
import homeOfficeImg from "@/assets/home-office.jpg";
import bedroomImg from "@/assets/bedroom.jpg";
import bathroomImg from "@/assets/providedassets/tile2.jpg";
import vasesImg from "@/assets/ceramic-vases.jpg";
import pendantImg from "@/assets/pendant-light.jpg";
import footerChairImg from "@/assets/footer-chair.jpg";
import sofaImg from "@/assets/sofa.jpg";
import chairImg from "@/assets/chair.jpg";
import tableImg from "@/assets/table.jpg";
import bedImg from "@/assets/bed.jpg";
import wardrobeImg from "@/assets/Minimal Glow Wardrobe Design ✨.jpg";
import decorImg from "@/assets/decor.jpg";
import lightingImg from "@/assets/softglamlights-categories.jpg";
import curtainImg from "@/assets/milky and ash White  Curtains for Ambient Light.jpg";
import tileImg from "@/assets/providedassets/tile1.jpg";
import otherImg from "@/assets/ceramic-vases.jpg";

const FEATURES = [
  {
    img: armchairImg,
    title: "Modern Furniture",
    body: "Sculptural seating and statement pieces crafted for warm, contemporary homes.",
  },
  {
    img: sculptureImg,
    title: "Decor Accents",
    body: "Elegant sculptures and objects that bring quiet character to every corner.",
  },
  {
    img: wabiSabiImg,
    title: "Inspired Living",
    body: "Wabi-sabi lighting and organic textures that turn rooms into sanctuaries.",
  },
];

const CATEGORIES = [
  { img: sofaImg, title: "Sofa" },
  { img: chairImg, title: "Chair" },
  { img: tableImg, title: "Table" },
  { img: bedImg, title: "Bed" },
  { img: wardrobeImg, title: "Wardrobe" },
  { img: decorImg, title: "Decor" },
  { img: lightingImg, title: "Lighting" },
  { img: curtainImg, title: "Curtain" },
  { img: tileImg, title: "Tile" },
  { img: otherImg, title: "Other" },
];

const COLLECTIONS = [
  { img: livingRoomImg, title: "Living Room", body: "Sofas, lounge chairs and calming green walls." },
  { img: homeOfficeImg, title: "Home Office", body: "Neutral desks and focused, serene workspaces." },
  { img: bedroomImg, title: "Bedroom", body: "Soft platform beds wrapped in warm ambient light." },
  { img: bathroomImg, title: "Decor Accessories", body: "Everyday essentials with a spa-like finish." },
];

const ARRIVALS = [
  { img: armchairImg, title: "Elegant Armchair", body: "Boucle curves with a walnut swivel base." },
  { img: vasesImg, title: "Ceramic Vases", body: "Minimalist stoneware in warm neutral glazes." },
  { img: pendantImg, title: "Minimalist Lamp", body: "Sculpted pendant light with a soft amber glow." },
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-[#EFEAE4] text-[#2B1B14]">
      {/* Nav */}
      <header className="sticky top-0 z-40 bg-[#EFEAE4]/90 backdrop-blur border-b border-[#947E64]/20">
        <nav className="mx-auto max-w-6xl px-4 md:px-8 py-4 flex items-center justify-between">
          <Link to="/" data-testid="brand-logo" className="font-serif-display text-2xl font-semibold tracking-tight text-[#2B1B14] inline-flex items-center gap-2">
            <span className="inline-block h-2 w-2 rounded-full bg-[#947E64]" />
            NEIV
          </Link>
          <div className="hidden md:flex items-center gap-8 text-sm text-[#624A36]">
            <a href="#home" className="hover:text-[#947E64] transition">Home</a>
            <a href="#categories" className="hover:text-[#947E64] transition">Categories</a>
            <a href="#features" className="hover:text-[#947E64] transition">About</a>
            <a href={CALENDLY_DEMO_URL} target="_blank" rel="noopener noreferrer" className="hover:text-[#947E64] transition">Book a Demo</a>
            <Link to="/contact" className="hover:text-[#947E64] transition">Contact Us</Link>
            {/* <Link to="/login" data-testid="nav-login" className="hover:text-[#947E64] transition">Log in</Link> */}
          </div>
          <Link
            to="/signup"
            data-testid="nav-signup"
            className="rounded-lg bg-[#947E64] px-5 py-2.5 text-sm font-medium text-white transition hover:bg-[#624A36]"
          >
            Get Started
          </Link>
        </nav>
      </header>

      {/* Hero — full-width image with overlay text */}
      <section id="home" className="relative overflow-hidden">
        <img
          src={heroImg}
          alt="Warm open-plan interior with wooden staircase"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#EFEAE4] via-[#EFEAE4]/70 to-[#EFEAE4]/10" />
        <div className="relative mx-auto max-w-6xl px-4 md:px-8 pt-24 md:pt-32 pb-40 md:pb-56">
          <h1 className="font-serif-display text-5xl sm:text-6xl md:text-[4.25rem] font-medium leading-[1.08] tracking-tight text-[#2B1B14] max-w-xl">
            Design Your
            <br />
            Dream Living Space
          </h1>
          <p className="mt-5 text-lg text-[#624A36] leading-relaxed max-w-md">
            Stylish furniture and decor to elevate your home.
          </p>
          <div className="mt-8">
            <Link
              to="/signup"
              data-testid="hero-cta-button"
              className="inline-flex items-center gap-2 rounded-lg bg-[#947E64] px-7 py-3.5 font-medium text-white transition hover:bg-[#624A36]"
            >
              Explore More <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* About Us */}
      <section id="features" className="relative z-10 bg-[#EFEAE4] pt-0 pb-20 md:pb-28">

        {/* Floating cards over hero */}
        <div className="mx-auto max-w-6xl px-4 md:px-8 -mt-28 md:-mt-36">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {FEATURES.map(({ img, title, body }) => (
              <div key={title} className="flex items-start gap-4 rounded-xl border border-[#947E64]/20 bg-white p-5 shadow-[0_10px_30px_rgba(43,27,20,0.12)]">
                <img src={img} alt={title} className="h-20 w-20 shrink-0 rounded-lg object-cover" />
                <div>
                  <h3 className="font-serif-display text-lg font-semibold text-[#2B1B14]">{title}</h3>
                  <p className="mt-1 text-sm text-[#624A36] leading-relaxed">{body}</p>
                  <Link to="/signup" className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-[#947E64] transition hover:text-[#624A36]">
                    Shop Now <ArrowRight className="h-3.5 w-3.5" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* About Us — full section */}
        <div className="mx-auto max-w-6xl px-4 md:px-8 mt-24 md:mt-32">

          {/* Overline + heading */}
          <div className="text-center max-w-2xl mx-auto">
            <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-[#947E64] mb-4">About Us</span>
            <h2 className="font-serif-display text-4xl md:text-5xl font-medium leading-tight tracking-tight text-[#2B1B14]">
              We believe every room deserves a story.
            </h2>
            <p className="mt-5 text-[#624A36] leading-relaxed text-lg">
              NEIV was built for furniture retailers who want to give their customers a truly immersive experience — visualizing pieces in their own homes before they buy.
            </p>
          </div>

          {/* Two-column image + text */}
          <div className="mt-16 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <img src={livingRoomImg} alt="Living room" className="rounded-2xl w-full h-80 object-cover shadow-[0_20px_60px_rgba(43,27,20,0.15)]" />
              <img src={armchairImg} alt="Armchair" className="absolute -bottom-8 -right-6 w-40 h-40 rounded-xl object-cover shadow-[0_10px_30px_rgba(43,27,20,0.2)] border-4 border-[#EFEAE4]" />
            </div>
            <div className="lg:pl-6">
              <h3 className="font-serif-display text-2xl md:text-3xl font-medium text-[#2B1B14] leading-snug">
                From showroom floor to living room — instantly.
              </h3>
              <p className="mt-4 text-[#624A36] leading-relaxed">
                Our AI-powered visualizer lets customers upload a photo of their room and see exactly how your furniture fits in — with real lighting, real proportions, and real confidence.
              </p>
              <p className="mt-3 text-[#624A36] leading-relaxed">
                We partner with retailers across India to bring this technology directly to their showrooms via QR codes, sales teams, and a seamless dashboard.
              </p>
              <a href={CALENDLY_DEMO_URL} target="_blank" rel="noopener noreferrer" className="mt-8 inline-flex items-center gap-2 rounded-lg bg-[#947E64] px-6 py-3 font-medium text-white transition hover:bg-[#624A36]">
                See it in action <ArrowRight className="h-4 w-4" />
              </a>
            </div>
          </div>

          {/* Stats row */}
          <div className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { value: "500+", label: "Retailers onboarded" },
              { value: "50k+", label: "Rooms visualized" },
              { value: "3×", label: "Higher conversion" },
              { value: "24h", label: "Support response" },
            ].map(({ value, label }) => (
              <div key={label} className="text-center p-6 rounded-xl border border-[#947E64]/20 bg-white shadow-[0_4px_20px_rgba(43,27,20,0.05)]">
                <p className="font-serif-display text-4xl font-semibold text-[#947E64]">{value}</p>
                <p className="mt-2 text-sm text-[#624A36]">{label}</p>
              </div>
            ))}
          </div>

          {/* Values row */}
          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { img: wabiSabiImg, title: "Crafted with care", body: "Every feature is designed around the real needs of furniture retailers and their customers — nothing superfluous." },
              { img: homeOfficeImg, title: "Built for India", body: "Razorpay payments, regional support, and pricing that works for stores of every size across the country." },
              { img: bedroomImg, title: "Always improving", body: "We ship updates weekly based on retailer feedback. Your store gets better without you lifting a finger." },
            ].map(({ img, title, body }) => (
              <div key={title} className="group overflow-hidden rounded-xl border border-[#947E64]/20 bg-white shadow-[0_4px_20px_rgba(43,27,20,0.05)]">
                <div className="overflow-hidden h-48">
                  <img src={img} alt={title} className="h-full w-full object-cover transition duration-500 group-hover:scale-105" />
                </div>
                <div className="p-6">
                  <h4 className="font-serif-display text-lg font-semibold text-[#2B1B14]">{title}</h4>
                  <p className="mt-2 text-sm text-[#624A36] leading-relaxed">{body}</p>
                </div>
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* Shop by Categories */}
      <section id="categories" className="mx-auto max-w-6xl px-4 md:px-8 pb-20 md:pb-28">
        <div className="text-center max-w-xl mx-auto mb-10">
          <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-[#947E64] mb-3">Browse</span>
          <h2 className="font-serif-display text-3xl md:text-4xl font-medium tracking-tight text-[#2B1B14]">
            Shop by Categories
          </h2>
          <p className="mt-3 text-sm text-[#624A36]">Find exactly what your space needs — from statement sofas to finishing touches.</p>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          {CATEGORIES.map(({ img, title }) => (
            <Link
              key={title}
              to="/signup"
              className="group relative overflow-hidden rounded-2xl shadow-[0_4px_20px_rgba(43,27,20,0.08)] aspect-[3/4]"
            >
              <img
                src={img}
                alt={title}
                className="absolute inset-0 h-full w-full object-cover transition duration-500 group-hover:scale-108"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#2B1B14]/75 via-[#2B1B14]/10 to-transparent" />
              <div className="absolute inset-x-0 bottom-0 p-4 flex items-end justify-between">
                <h3 className="font-serif-display text-base font-semibold text-[#EFEAE4] leading-tight">{title}</h3>
                <ArrowRight className="h-4 w-4 text-[#947E64] shrink-0 transition group-hover:translate-x-1" />
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Featured Collections — commented out */}

      {/* Latest Arrivals — dark section */}
      <section id="arrivals" className="bg-[#2B1B14] py-20 md:py-28">
        <div className="mx-auto max-w-6xl px-4 md:px-8 grid grid-cols-1 lg:grid-cols-[1fr_2fr] gap-12 items-start">
          <div className="flex flex-col">
            <h2 className="font-serif-display text-3xl md:text-4xl font-medium tracking-tight text-[#EFEAE4]">
              Latest Arrivals
            </h2>
            <p className="mt-4 text-[#EFEAE4]/70 leading-relaxed max-w-sm">
              New pieces landing this season — warm woods, soft ceramics and sculpted light for every room of the home.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/signup"
                className="self-start inline-flex items-center gap-2 rounded-lg bg-[#947E64] px-6 py-3 font-medium text-white transition hover:bg-[#EFEAE4] hover:text-[#2B1B14]"
              >
                Get Inspired <ArrowRight className="h-4 w-4" />
              </Link>
              <a
                href={CALENDLY_DEMO_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="self-start inline-flex items-center gap-2 rounded-lg border border-[#947E64] px-6 py-3 font-medium text-[#EFEAE4] transition hover:bg-[#947E64] hover:text-white"
              >
                Book a Demo <ArrowRight className="h-4 w-4" />
              </a>
            </div>
            <img
              src={footerChairImg}
              alt="Sculptural wood and chrome lounge chair"
              className="mt-10 w-full max-h-64 rounded-xl object-cover shadow-[0_10px_30px_rgba(0,0,0,0.35)]"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            {ARRIVALS.map(({ img, title, body }) => (
              <div key={title} className="flex flex-col overflow-hidden rounded-xl bg-[#EFEAE4] shadow-[0_10px_30px_rgba(0,0,0,0.25)]">
                <img src={img} alt={title} className="h-44 w-full object-cover" />
                <div className="p-4">
                  <h3 className="font-serif-display text-base font-semibold text-[#2B1B14]">{title}</h3>
                  <p className="mt-1 text-xs text-[#624A36] leading-relaxed">{body}</p>
                  <Link to="/signup" className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-[#947E64] transition hover:text-[#624A36]">
                    View Product <ArrowRight className="h-3 w-3" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer id="contact" className="bg-[#2B1B14] border-t border-[#624A36] py-8 text-[#EFEAE4]/70">
        <div className="mx-auto max-w-6xl px-4 md:px-8 flex flex-col md:flex-row items-center justify-between gap-4">
            <Link to="/" className="font-serif-display text-2xl font-semibold tracking-tight text-[#EFEAE4] inline-flex items-center gap-2">
              <span className="inline-block h-2 w-2 rounded-full bg-[#947E64]" />
              NEIV
            </Link>
            <div className="flex gap-8 text-sm">
              <Link to="/login" className="transition hover:text-[#947E64]">Retailer Login</Link>
              <Link to="/signup" className="transition hover:text-[#947E64]">Sign up</Link>
            </div>
            <p className="text-xs">© {new Date().getFullYear()} NEIV. Built for interiors.</p>
        </div>
      </footer>
    </div>
  );
}
